/**
 * The utilization report (BUILD_PLAN.md section 13.1; ADR 0146 decision 3):
 * each tech's hours by day, week or month of the filters' days, each in their
 * own timezone, as the API counts them:
 *
 * - **Available:** 8 hours on each workday (Settings; ADR 0201).
 * - **Captured:** their activities in the blocks the day view counts, two
 *   clients at once counted once.
 * - **Logged:** submitted blocks, and time logged directly in Autotask; of
 *   which on open tickets, said under it (ADR 0185).
 * - **Utilization:** logged over available; none with nothing available.
 * - **Capture gap:** each day's captured time not logged, added up.
 *
 * Day, week or month is the report's own choice, kept in the URL (`by`) as
 * the filters change. "Download CSV" saves the same rows.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useId } from "react";
import { useSearchParams } from "react-router";

import type { Grouping, TechUtilization, UtilizationPeriod, UtilizationReport as Report } from "../../api/types";
import { useSession } from "../../app/session-context";
import { Panel } from "../../components/ui/Panel";
import { formatDayParts, formatShortDay } from "../../lib/dates";
import { saveBlob } from "../../lib/download";
import { formatDuration } from "../../lib/durations";
import { possessive } from "../../lib/possessive";
import { errorMessage, pillButton, retryOnce } from "./common";
import { useReportFilters } from "./filters";

const GROUPINGS: { by: Grouping; label: string }[] = [
  { by: "day", label: "Day" },
  { by: "week", label: "Week" },
  { by: "month", label: "Month" },
];

const DAY_NAMES: Record<Report["workdays"][number], string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

const headCell = "pb-2 pr-3";
const COLUMNS = ["Available", "Captured", "Logged", "Utilization", "Capture gap"] as const;

function techName(tech: TechUtilization): string {
  return tech.user.display_name ?? tech.user.email ?? "Unnamed user";
}

function groupingIn(value: string | null): Grouping {
  return value === "day" || value === "month" ? value : "week";
}

/** "Mon, Oct 5", "Oct 5 – Oct 11" or "October 2026": a period as it's grouped. */
function periodLabel(period: UtilizationPeriod, by: Grouping): string {
  if (by === "day") {
    return formatShortDay(period.start);
  }
  if (by === "month") {
    const [y, m] = period.start.split("-").map(Number) as [number, number];
    return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" }).format(
      new Date(Date.UTC(y, m - 1, 1)),
    );
  }
  return `${formatDayParts(period.start).date} – ${formatDayParts(period.end).date}`;
}

/** The workdays as a phrase: "Monday to Friday", or each named. */
function workdaysPhrase(days: Report["workdays"]): string {
  const order = Object.keys(DAY_NAMES) as Report["workdays"];
  const first = days[0];
  const last = days.at(-1);
  if (first !== undefined && last !== undefined && days.length > 2) {
    const run = order.slice(order.indexOf(first), order.indexOf(last) + 1);
    if (run.length === days.length && run.every((day, i) => day === days[i])) {
      return `${DAY_NAMES[first]} to ${DAY_NAMES[last]}`;
    }
  }
  const names = days.map((day) => DAY_NAMES[day]);
  return names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names.at(-1) ?? ""}`;
}

function Percent({ percent }: { percent: number | null }) {
  if (percent === null) {
    return (
      <span>
        <span aria-hidden="true">—</span>
        <span className="sr-only">no hours available</span>
      </span>
    );
  }
  return <span className="font-semibold tracking-[-0.02em]">{`${percent.toFixed(1)}%`}</span>;
}

function Cells({ period }: { period: UtilizationPeriod }) {
  return (
    <>
      <td className="py-3 pr-3 whitespace-nowrap">{formatDuration(period.available_seconds)}</td>
      <td className="py-3 pr-3 whitespace-nowrap">{formatDuration(period.captured_seconds)}</td>
      <td className="py-3 pr-3 whitespace-nowrap">
        {formatDuration(period.logged_seconds)}
        {/* Of which on tickets not closed yet (ADR 0185): the maths don't change. */}
        {period.open_ticket_seconds > 0 ? (
          <span className="block text-xs text-muted">{`${formatDuration(period.open_ticket_seconds)} on open tickets`}</span>
        ) : null}
      </td>
      <td className="py-3 pr-3 whitespace-nowrap">
        <Percent percent={period.percent} />
      </td>
      <td className="py-3 whitespace-nowrap">{formatDuration(period.gap_seconds)}</td>
    </>
  );
}

function Head({ first }: { first: string }) {
  return (
    <thead className="text-[12.5px] font-medium tracking-[0.08em] text-muted uppercase">
      <tr>
        <th scope="col" className={headCell}>
          {first}
        </th>
        {COLUMNS.map((column, i) => (
          <th key={column} scope="col" className={i === COLUMNS.length - 1 ? "pb-2" : headCell}>
            {column}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/** Each tech's total over the days. */
function Summary({ techs }: { techs: TechUtilization[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-[13.5px]">
        <caption className="sr-only">Each tech&apos;s utilization over the days</caption>
        <Head first="Tech" />
        <tbody>
          {techs.map((tech) => (
            <tr key={tech.user.id} className="border-t border-line align-top">
              <th scope="row" className="py-3 pr-3 font-medium">
                {techName(tech)}
              </th>
              <Cells period={tech.total} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TechSection({ tech, by, workdays }: { tech: TechUtilization; by: Grouping; workdays: Report["workdays"] }) {
  const headingId = useId();
  const name = techName(tech);
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 id={headingId} className="text-base font-[650] tracking-[-0.015em]">
          {name}
        </h3>
        <p className="text-[13px] text-muted">
          {[
            `${formatDuration(tech.capacity_seconds)} a day, ${workdaysPhrase(workdays)}`,
            `Days in ${tech.user.timezone}`,
          ].join(" · ")}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[13.5px]">
          <caption className="sr-only">{`${possessive(name)} utilization by ${by}`}</caption>
          <Head first={GROUPINGS.find((g) => g.by === by)?.label ?? "Period"} />
          <tbody>
            {tech.periods.map((period) => (
              <tr key={period.start} className="border-t border-line align-top">
                <th scope="row" className="py-3 pr-3 font-medium whitespace-nowrap">
                  {periodLabel(period, by)}
                </th>
                <Cells period={period} />
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-line align-top">
              <th scope="row" className="py-3 pr-3 font-semibold">
                Total
              </th>
              <Cells period={tech.total} />
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

export function UtilizationReport() {
  const { api } = useSession();
  const headingId = useId();
  const byId = useId();
  const [filters] = useReportFilters();
  const [params, setParams] = useSearchParams();
  const by = groupingIn(params.get("by"));
  const report = useQuery({
    queryKey: ["reports", "utilization", filters, by],
    queryFn: () => api.getUtilizationReport(filters, by),
    retry: retryOnce,
  });
  const download = useMutation({
    mutationFn: () => api.downloadUtilizationCsv(filters, by),
    onSuccess: (blob) => {
      saveBlob(blob, `utilization-${filters.start}-to-${filters.end}.csv`);
    },
  });

  return (
    <Panel aria-labelledby={headingId} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 id={headingId} className="text-lg font-semibold tracking-tight">
            Utilization
          </h2>
          <p className="text-[13.5px] text-muted">
            Logged hours over the hours available: 8 on each workday. Two clients at once count once.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div role="radiogroup" aria-labelledby={byId} className="flex items-center gap-1">
            <span id={byId} className="mr-1 text-xs font-semibold text-muted">
              By
            </span>
            {GROUPINGS.map((grouping) => (
              <label
                key={grouping.by}
                className={
                  "flex h-9 cursor-pointer items-center rounded-pill px-3 text-sm " +
                  (grouping.by === by
                    ? "bg-brand-tint font-semibold text-brand-ink-on-tint"
                    : "font-medium text-muted hover:text-text")
                }
              >
                <input
                  type="radio"
                  name="utilization-by"
                  className="sr-only"
                  checked={grouping.by === by}
                  onChange={() => {
                    const next = new URLSearchParams(params);
                    next.set("by", grouping.by);
                    setParams(next, { replace: true });
                  }}
                />
                {grouping.label}
              </label>
            ))}
          </div>
          <button
            type="button"
            className={pillButton}
            disabled={!report.isSuccess || download.isPending}
            onClick={() => {
              download.mutate();
            }}
          >
            Download CSV
          </button>
        </div>
      </div>
      {download.isError ? (
        <p role="alert" className="text-sm text-status-needs-client-fg">
          Not downloaded: {errorMessage(download.error)}
        </p>
      ) : null}
      {report.isPending ? (
        <p role="status" className="text-muted">
          Loading the report…
        </p>
      ) : report.isError ? (
        <p role="alert" className="text-status-needs-client-fg">
          {errorMessage(report.error)}
        </p>
      ) : report.data.techs.length === 0 ? (
        <p className="py-6 text-center text-muted">No one to report on.</p>
      ) : (
        <>
          <Summary techs={report.data.techs} />
          {report.data.techs.map((tech) => (
            <TechSection key={tech.user.id} tech={tech} by={report.data.by} workdays={report.data.workdays} />
          ))}
        </>
      )}
    </Panel>
  );
}
